// 1.3.93: YUIChat 项目 - 试用过期提示弹窗组件
// 1.3.94: 将橘黄色警告背景改为YUI紫色主题，保持整体UI/UX一致性
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Clock, Crown } from 'lucide-react';

interface TrialExpiredModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function TrialExpiredModal({ isOpen, onClose }: TrialExpiredModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleUpgrade = () => {
    onClose();
    navigate('/account');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* 背景遮罩 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
          />
          {/* 弹窗内容 */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
              {/* 1.3.94: 头部 - YUI紫色主题背景 */}
              <div className="bg-gradient-to-r from-primary to-purple-600 p-6 text-white">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <Clock className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">
                      {t('trialExpiredTitle', '免费试用已到期')}
                    </h2>
                    <p className="text-white/80 text-sm mt-1">
                      {t('trialExpiredSubtitle', '升级套餐以继续使用')}
                    </p>
                  </div>
                </div>
              </div>

              {/* 内容 */}
              <div className="p-6">
                <p className="text-gray-600 mb-4">
                  {t('trialExpiredMessage', '您的免费试用期已结束。您的项目数据已保存，升级套餐后即可继续使用所有功能。')}
                </p>
                
                {/* 1.3.94: 功能限制说明 - 使用浅紫色背景保持主题一致 */}
                <div className="bg-purple-50 rounded-lg p-4 mb-4 border border-purple-100">
                  <p className="text-purple-700 font-medium mb-2">
                    {t('trialExpiredRestrictions', '当前限制：')}
                  </p>
                  <ul className="text-sm text-purple-600 space-y-1">
                    <li>• {t('trialExpiredNoCreate', '无法创建新项目')}</li>
                    <li>• {t('trialExpiredNoAccess', '无法访问已有项目')}</li>
                    <li>• {t('trialExpiredDataSafe', '项目数据安全保存')}</li>
                  </ul>
                </div>
              </div>

              {/* 底部按钮 */}
              <div className="flex items-center justify-end gap-3 p-6 pt-0">
                <button
                  onClick={onClose}
                  className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t('cancel', '取消')}
                </button>
                <button
                  onClick={handleUpgrade}
                  className="px-6 py-2.5 text-white bg-gradient-to-r from-primary to-primary-dark rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2"
                >
                  <Crown className="w-4 h-4" />
                  {t('upgradeNow', '立即升级')}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
